// Excel експортът на „Продажби по периоди".
//
// Редовете идват ФИЛТРИРАНИ и ПОДРЕДЕНИ от таблицата (`onFilteredRowsChange`)
// — файлът повтаря екрана. Числата остават ЧИСЛА. Тук влизат всички колони,
// включително изгасените на екрана: сверката с касата се прави в таблица,
// където няма борба за ширина.

import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import type { StationColumn } from "./salesByPeriodsColumns";
import type { PeriodRow } from "./salesByPeriodsMath";

type Options = {
  rows: PeriodRow[];
  methodEntries: MethodEntry[];
  stationColumns: StationColumn[];
  t: (key: string, options?: Record<string, unknown>) => string;
};

const round = (value: number) => Number(value.toFixed(2));

export const exportSalesByPeriods = ({
  rows,
  methodEntries,
  stationColumns,
  t,
}: Options) => {
  const columns: ExcelColumn<PeriodRow>[] = [
    {
      header: t("reportsPage.salesPeriodsPeriodColumn"),
      value: (row) => (row.sublabel ? `${row.label} · ${row.sublabel}` : row.label),
      width: 26,
    },
    { header: t("reportsPage.salesPeriodsBillsColumn"), value: (row) => row.bills, width: 10 },
    { header: t("reportsPage.salesPeriodsQuantityColumn"), value: (row) => Number(row.quantity.toFixed(3)), width: 12 },
    { header: t("reportsPage.salesPeriodsRevenueColumn"), value: (row) => round(row.revenue), width: 14 },
    { header: t("reportsPage.salesPeriodsFiscalizedColumn"), value: (row) => round(row.fiscalized), width: 14 },
    { header: t("reportsPage.salesPeriodsVatColumn"), value: (row) => round(row.vatAmount), width: 12 },
    { header: t("reportsPage.salesPeriodsNetColumn"), value: (row) => round(row.netAmount), width: 14 },
    { header: t("reportsPage.salesPeriodsAvgPriceColumn"), value: (row) => round(row.avgPrice), width: 12 },
    { header: t("reportsPage.salesPeriodsAvgBillColumn"), value: (row) => round(row.avgBill), width: 12 },
    { header: t("reportsPage.salesPeriodsClientsColumn"), value: (row) => row.clients, width: 12 },
    { header: t("reportsPage.salesPeriodsClientRevenueColumn"), value: (row) => round(row.clientRevenue), width: 14 },
    { header: t("reportsPage.salesPeriodsClientAvgColumn"), value: (row) => round(row.clientAvg), width: 18 },
  ];

  for (const entry of methodEntries) {
    columns.push(
      {
        header: entry.label,
        value: (row) => round(row.methodAmounts.get(entry.id) ?? 0),
        width: 14,
      },
      {
        header: t("reportsPage.methodCountColumn", { method: entry.label }),
        value: (row) => row.methodCounts.get(entry.id) ?? 0,
        width: 12,
      },
    );
  }

  for (const station of stationColumns) {
    columns.push({
      header: station.label,
      value: (row) => round(row.stationAmounts.get(station.id) ?? 0),
      width: 14,
    });
  }

  columns.push(
    { header: t("reportsPage.salesPeriodsBaseColumn"), value: (row) => round(row.baseAmount), width: 14 },
    { header: t("reportsPage.salesPeriodsDiscountColumn"), value: (row) => round(row.discount), width: 14 },
    { header: t("reportsPage.salesPeriodsMarkupColumn"), value: (row) => round(row.markup), width: 14 },
  );

  exportToExcel<PeriodRow>({
    filename: t("reportsPage.salesPeriodsPageTitle"),
    sheetName: t("reportsPage.salesPeriodsReport"),
    columns,
    rows,
  });
};
